import type { AnyDataPayload, ColumnDefinition } from './unfurl';

/**
 * Google Drive *folder* support.
 *
 * A Google Drive folder URL resolves to a collection of files. The backend
 * streams the same SSE shape as a YouTube playlist (`columnDefinition` +
 * `entities` + a final metadata event), and right now we treat it as
 * functionally identical to a playlist: extract every file and upload them all.
 *
 * It is kept as a SEPARATE case (its own engine, type, guard, and the
 * `buildGoogleDriveFolderData` parse function below) so the two can diverge
 * later without untangling them. The shared machinery — the SSE reader in
 * `unfurl.ts` and `convertUnfurlDataToUnfurlItem` — stays DRY; only the bits
 * that are genuinely Drive-folder-specific live here.
 */

/** Engine tag distinguishing a Drive folder from `youtube:playlist` etc. */
export const GOOGLE_DRIVE_FOLDER_ENGINE = 'googledrive:folder';

/** The `type` value of the final SSE metadata event for a Drive folder. */
export const GOOGLE_DRIVE_FOLDER_METADATA_TYPE = 'folder';

/**
 * Entity for a file inside a Drive folder. Note the lowercase `googledrive`
 * extractor — that is the actual wire format from the folder endpoint. (The
 * older single-file `GoogleDriveFileEntity` in unfurl.ts uses `'GoogleDrive'`;
 * match the bytes the API actually sends here.)
 */
export interface GoogleDriveFolderEntity {
  extractor: 'googledrive';
  id: string;
  url: string;
}

/**
 * Final `metadata` SSE event for a folder. The wire also carries a `fileCount`,
 * but we deliberately don't model/consume it: every count shown to the user is
 * derived from the actual number of extracted items (like the YouTube path),
 * so the metadata count is redundant. `id`/`title` are what we use (title names
 * the collection).
 */
export interface GoogleDriveFolderMetadata {
  type: typeof GOOGLE_DRIVE_FOLDER_METADATA_TYPE;
  id: string;
  title: string;
}

/** One file row from the folder stream. */
export interface GoogleDriveFolderItem {
  entity: GoogleDriveFolderEntity;
  columns: Record<string, unknown>;
}

/**
 * Accumulated folder data, shaped like `YoutubeMultipleData` so the shared
 * `convertUnfurlDataToUnfurlItem` (its `type: 'multiple'` branch) handles it
 * unchanged.
 */
export interface GoogleDriveFolderData {
  type: 'multiple';
  data: {
    engine: typeof GOOGLE_DRIVE_FOLDER_ENGINE;
    items: GoogleDriveFolderItem[];
    columnDefinitions: ColumnDefinition[];
    metadata: GoogleDriveFolderMetadata;
  };
}

/**
 * Whether an SSE metadata event is a Drive-folder metadata event. Used by the
 * shared SSE parser to decide it's looking at a folder rather than a YouTube
 * channel/playlist.
 */
export function isGoogleDriveFolderMetadata(
  metadata: {
    type?: string;
  } | null,
): metadata is GoogleDriveFolderMetadata {
  return metadata?.type === GOOGLE_DRIVE_FOLDER_METADATA_TYPE;
}

/**
 * Builds the final `GoogleDriveFolderData` payload from the SSE accumulator.
 * This is the Drive-folder analogue of the inline `YoutubeMultipleData` build
 * in `parseSSEStream`. Entities are accepted loosely (the parser accumulates
 * them with a YouTube entity type) and re-tagged to the folder entity shape.
 */
export function buildGoogleDriveFolderData(
  columnDefinitions: ColumnDefinition[],
  entities: Array<{
    entity: { extractor: string; id: string; url: string };
    columns: Record<string, unknown>;
  }>,
  metadata: GoogleDriveFolderMetadata,
): GoogleDriveFolderData {
  return {
    type: 'multiple',
    data: {
      engine: GOOGLE_DRIVE_FOLDER_ENGINE,
      items: entities.map(({ entity, columns }) => ({
        entity: { extractor: 'googledrive', id: entity.id, url: entity.url },
        columns,
      })),
      columnDefinitions,
      metadata,
    },
  };
}

/** Type guard: is this payload a Google Drive folder collection? */
export function isGoogleDriveFolderData(data: AnyDataPayload): data is GoogleDriveFolderData {
  return (
    data.type === 'multiple' &&
    'engine' in data.data &&
    data.data.engine === GOOGLE_DRIVE_FOLDER_ENGINE
  );
}
